import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  arrowHeadLeftSVG,
  bulbSVG,
  chartBarSVG,
  switchShopSVG,
  ToggleSideBarSVG,
  userSVG,
  tickWithCircleSVG,
} from '../../lib/allCustomSVGs';
import '../../styles/components/sidebar/sidebarTara.scss';
import CustomSVGs from '../../ui/customSVGs/CustomSVGs';
import store, { RootState } from '../../store/store';
import { useTranslation } from 'react-i18next';
import { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  linksDashboard,
  linksIntegrations,
  linksRecommendations,
  linksSettings,
  linksTracking,
  linksAIOptimizations,
  linksReports,
  linksActions,
} from '../../lib/constants/sidebarLinks';
import { allRoutes } from '../../lib/constants/route';
import SidebarProfileTheme2 from './SidebarProfileTheme2';
import truncate from '../../lib/helper-functions/truncate';
import TaraHistorySidebar from '../../pages/tara/history/TaraHistorySidebar';
import TaraCreateNewChat from '../../pages/tara/history/TaraCreateNewChat';
import {
  setIsPaymentPopupOpen,
  setIsSwitchShopPopupOpen,
} from '../../store/reducers/uiSlice';
import isNotEmptyString from '../../lib/helper-functions/isNotEmptyString';

interface Props {
  isSidebarMinimized: boolean;
  changeSidebarSize: () => void;
}

interface SidebarLink {
  title: string;
  link: string;
  icon: any;
  iconType: string;
  specialURL?: string;
  urlName?: string;
}

interface MenuItemProps {
  item: SidebarLink;
  currentPath: string;
  isSidebarMinimized: boolean;
  isNested: boolean;
  navigate?: (path: string) => void;
  onClick?: () => void;
  isModal?: boolean;
}

interface NestedMenuItemProps extends Omit<MenuItemProps, 'item' | 'isNested'> {
  isOpen: boolean;
  onToggle: () => void;
  icon: any;
  title: string;
  children?: SidebarLink[];
  route: keyof typeof allRoutes;
}

const SidebarTara = ({ isSidebarMinimized, changeSidebarSize }: Props) => {
  const isDarkMode = useSelector((state: RootState) => state.UI.theme.dark);
  const userProfile = useSelector((state: RootState) => state.user.profile);
  const taraTrialDetails = useSelector(
    (state: RootState) => state.user.shop.taraTrialDetails
  ) || { is_trial: false, trial_available_days: undefined };
  const staticURL = useSelector((state: RootState) => state.app.staticURL);
  const merchantTheme = useSelector(
    (state: RootState) => state.user.merchant.config
  );
  const isTrackingScriptConnectionSuccess = useSelector(
    (state: RootState) =>
      state.user?.isTrackingScriptConnectionSuccess ?? undefined
  );
  const isTaraChatAnimationEnabled = useSelector(
    (state: RootState) => state.UI.isTaraChatAnimationEnabled
  );
  const currentChat = useSelector((state: RootState) => state.chat.currentChat);
  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = location.pathname;
  const { t } = useTranslation();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [openedNestedMenuLinks, setOpenedNestedMenuLinks] = useState<string[]>([
    currentPath.split('/')[1],
  ]);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const activeShopName = userProfile?.all_shops?.find(
    (shop: any) => shop?.slug === userProfile?.active_shop_slug
  )?.name;

  // Toggle dropdown open/close state
  const toggleDropdown = () => {
    setIsDropdownOpen((prev) => !prev);
  };

  // Effect to handle click outside to close the user dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };

    // Attach event listener to the document
    document.addEventListener('mousedown', handleClickOutside);

    // Clean up the event listener on component unmount
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const nestedMenuLink = currentPath.split('/')[1];
    setOpenedNestedMenuLinks([...openedNestedMenuLinks, nestedMenuLink]);
  }, [currentPath]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentPath]);

  const MenuItem = ({
    item,
    currentPath,
    isSidebarMinimized,
    isNested,
    navigate,
    onClick,
    isModal,
  }: MenuItemProps) => {
    if (!allRoutes[item.link as keyof typeof allRoutes] && !isModal) {
      return null;
    }

    return (
      <motion.li
        title={t(item.title)}
        initial={{ opacity: 0.8 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0.8 }}
        transition={{ duration: 0.1, ease: 'backInOut' }}
        onClick={() => {
          if (onClick) {
            onClick();
          } else {
            navigate && navigate(item.link);
          }
        }}
        data-sub_page_link_holder={
          !isModal && item.specialURL
            ? currentPath === item.specialURL
            : !isModal && currentPath.includes(item.urlName || '')
            ? 'true'
            : 'false'
        }
        data-is_nested_link_holder={isNested ? 'true' : 'false'}
        className='sidebarTara__bottom--linkItem'
      >
        <div className='sidebarTara__bottom--linkItem-left'>
          <span className='sidebarTara__bottom--linkItem-left-highlighter'></span>
          {item.iconType === 'svg' ? (
            <CustomSVGs svg={item.icon} />
          ) : (
            <img
              src={item.icon}
              className='subPageLinkHolderItems__img'
              alt='icon'
            />
          )}
          <motion.span
            initial={{ opacity: 0.8 }}
            animate={{ opacity: isSidebarMinimized ? 0 : 1 }}
            transition={{ duration: 0.1 }}
          >
            {t(item.title)}
          </motion.span>
        </div>
      </motion.li>
    );
  };

  const NestedMenuItem = ({
    isOpen,
    onToggle,
    icon,
    title,
    children,
    currentPath,
    isSidebarMinimized,
    navigate,
    route,
  }: NestedMenuItemProps) => {
    return (
      <>
        <motion.li
          title={t(title)}
          initial={{ opacity: 0.8 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0.8 }}
          transition={{ duration: 0.1, ease: 'backInOut' }}
          onClick={onToggle}
          key={title}
          className='sidebarTara__bottom--linkItem'
          data-sub_page_link_holder={
            currentPath?.startsWith(route) && !isOpen ? 'true' : 'false'
          }
          data-is-nested-link-open={isOpen ? 'true' : 'false'}
        >
          <div className='sidebarTara__bottom--linkItem-left'>
            <span className='sidebarTara__bottom--linkItem-left-highlighter'></span>
            <CustomSVGs svg={icon} />
            <motion.span
              initial={{ opacity: 0.8 }}
              animate={{ opacity: isSidebarMinimized ? 0 : 1 }}
              transition={{ duration: 0.1 }}
            >
              {t(title)}
            </motion.span>
          </div>
          <div className='sidebarTara__bottom--linkItem-right'>
            <CustomSVGs svg={arrowHeadLeftSVG} />
          </div>
        </motion.li>
        {isOpen &&
          children?.map((item, i) => (
            <MenuItem
              key={i}
              item={item}
              currentPath={currentPath}
              isSidebarMinimized={isSidebarMinimized}
              isNested={true}
              navigate={navigate}
            />
          ))}
      </>
    );
  };

  const renderSectionView = (
    links: SidebarLink[],
    route: keyof typeof allRoutes,
    options?: {
      isNested?: boolean;
      icon?: any;
      title?: string;
    }
  ) => {
    if (!allRoutes[route]) return null;

    if (options?.isNested) {
      const routeName = route.replace('/', '');
      return (
        <AnimatePresence>
          <NestedMenuItem
            isOpen={openedNestedMenuLinks.includes(routeName)}
            onToggle={() => {
              if (openedNestedMenuLinks.includes(routeName)) {
                setOpenedNestedMenuLinks(
                  openedNestedMenuLinks.filter((item) => item !== routeName)
                );
              } else {
                setOpenedNestedMenuLinks([...openedNestedMenuLinks, routeName]);
              }
            }}
            icon={options.icon}
            title={options.title || ''}
            children={links}
            currentPath={currentPath}
            isSidebarMinimized={isSidebarMinimized}
            navigate={navigate}
            route={route}
          />
        </AnimatePresence>
      );
    }

    return (
      <AnimatePresence>
        {links?.map((item, i) => (
          <MenuItem
            key={i}
            item={item}
            currentPath={currentPath}
            isSidebarMinimized={isSidebarMinimized}
            isNested={false}
            navigate={navigate}
          />
        ))}
      </AnimatePresence>
    );
  };

  /* --------------------------- Main Menu ---------------------------- */
  const dashboardSectionView = () =>
    renderSectionView(linksDashboard, '/dashboard');
  const integrationsSectionView = () =>
    renderSectionView(linksIntegrations, '/integrations');
  const reportsSectionView = () =>
    renderSectionView(linksReports, '/reports', {
      isNested: true,
      icon: bulbSVG,
      title: 'Reports',
    });
  const aiOptimizationsSectionView = () =>
    renderSectionView(linksAIOptimizations, '/ai-optimizations', {
      isNested: true,
      icon: chartBarSVG,
      title: 'AI Optimizations',
    });
  const actionsSectionView = () =>
    renderSectionView(linksActions, '/actions', {
      isNested: true,
      icon: tickWithCircleSVG,
      title: 'Actions',
    });
  const recommendationsSectionView = () =>
    renderSectionView(linksRecommendations, '/recommendations');

  /* --------------------------- Tracking Section ---------------------------- */
  const trackingSectionView = () =>
    renderSectionView(linksTracking, '/tracking');

  /* --------------------------- Settings Section ---------------------------- */
  const settingsSectionView = () =>
    renderSectionView(linksSettings, '/settings');

  /* --------------------------- Render Logo ---------------------------- */
  const RenderLogo = () => {
    if (
      merchantTheme.merchant_logo_light &&
      merchantTheme.merchant_logo_dark &&
      merchantTheme.merchant_name === 'Buckaroo'
    ) {
      return (
        <img
          src={`${staticURL}/medium/${
            isDarkMode
              ? merchantTheme.merchant_logo_light
              : merchantTheme.merchant_logo_dark
          }`}
          alt='logo'
        />
      );
    } else if (
      merchantTheme.merchant_logo_dark &&
      merchantTheme.merchant_logo_light
    ) {
      return (
        <img
          src={`${staticURL}/medium/${
            isDarkMode
              ? merchantTheme.merchant_logo_light
              : merchantTheme.merchant_logo_dark
          }`}
          alt='logo'
        />
      );
    } else {
      return (
        <img
          style={{ borderRadius: '1rem' }}
          src={`${staticURL}/medium/default.jpg`}
          alt='logo'
        />
      );
    }
  };

  /* --------------------------- Get Profile Name ---------------------------- */
  const getProfileName = () => {
    if (userProfile?.first_name || userProfile?.last_name) {
      return `${userProfile?.first_name} ${userProfile?.last_name}`;
    } else if (userProfile?.email) {
      return `${userProfile.email}`;
    } else {
      return t('User');
    }
  };

  const renderSideBar = () => {
    return (
      <ul className='sidebarTara__bottom'>
        <div
          style={{ marginTop: '1.5rem' }}
          className='taraHistorySidebar--title1'
        >
          <span>{t('Tara')}</span>
        </div>
        <div className='sidebarTara__bottom--links'>
          {/* Main Menu */}
          {!currentPath?.startsWith('/settings') &&
            !currentPath?.startsWith('/tracking') && (
              <>
                {/* Dashboard Section */}
                {isTrackingScriptConnectionSuccess &&
                isTrackingScriptConnectionSuccess === 'true'
                  ? dashboardSectionView()
                  : null}
                {/* Integrations (MCP) Section */}
                {integrationsSectionView()}
                {/* Actions (Approve/Deny) Section */}
                {actionsSectionView()}
                {/* Reports Section */}
                {reportsSectionView()}
                {/* AI Optimizations Section */}
                {aiOptimizationsSectionView()}
                {/* Recommendations Section */}
                {recommendationsSectionView()}
              </>
            )}

          {/* Tracking Section */}
          {currentPath?.startsWith('/tracking') && <>{trackingSectionView()}</>}

          {/* Settings Section */}
          {currentPath?.startsWith('/settings') && <>{settingsSectionView()}</>}
        </div>
        <div
          style={{ marginTop: '2rem' }}
          className='taraHistorySidebar--title1'
        >
          <span>{t('RecentChats')}</span>
        </div>
        <div className='sidebarTara__bottom--history'>
          <TaraHistorySidebar isSidebarMinimized={isSidebarMinimized} />
        </div>
      </ul>
    );
  };

  return (
    <div
      data-is-sidebar-minimized={isSidebarMinimized?.toString()}
      className='sidebarTara'
    >
      <div className='sidebarTara__top'>
        <div className='sidebarTara__top--left'>{RenderLogo()}</div>

        <div onClick={changeSidebarSize} className='sidebarTara__top--right'>
          <CustomSVGs svg={ToggleSideBarSVG} />
        </div>
      </div>
      <TaraCreateNewChat isSidebarMinimized={isSidebarMinimized} />

      {renderSideBar()}

      <div
        data-is-sidebar-profile-open={isDropdownOpen.toString()}
        className='sidebarTara__profile'
      >
        {/* Trial End Banner */}
        {taraTrialDetails &&
          taraTrialDetails?.is_trial &&
          taraTrialDetails?.trial_available_days &&
          taraTrialDetails?.trial_available_days > 0 && (
            <div
              className='sidebarTara__banner'
              onClick={() => {
                store.dispatch(setIsPaymentPopupOpen(true));
              }}
            >
              <div className='sidebarTara__banner--left'>
                <h3>
                  {isSidebarMinimized
                    ? '⏱︎ ' + taraTrialDetails?.trial_available_days
                    : t('TrialEndsInDays', {
                        days: taraTrialDetails?.trial_available_days,
                      })}
                </h3>
              </div>
            </div>
          )}

        {/* Switch Shop Modal */}
        {userProfile &&
          userProfile?.role === 'agency' &&
          userProfile?.all_shops &&
          Array.isArray(userProfile?.all_shops) &&
          userProfile?.all_shops?.length > 0 && (
            <AnimatePresence>
              <MenuItem
                key={'switch-shop'}
                item={{
                  icon: switchShopSVG,
                  iconType: 'svg',
                  title:
                    activeShopName && isNotEmptyString(activeShopName)
                      ? truncate(activeShopName, 17)
                      : t('SwitchShop'),
                  link: '/clients',
                }}
                currentPath={currentPath}
                isSidebarMinimized={isSidebarMinimized}
                isNested={false}
                isModal={false}
                onClick={() => {
                  navigate('/clients');
                }}
              />
            </AnimatePresence>
          )}

        {/* Profile Dropdown */}
        <div
          className='sidebarTara__profile--content'
          onClick={toggleDropdown}
          ref={dropdownRef}
        >
          <div className='sidebarTara__profile--content-left'>
            <CustomSVGs svg={userSVG} />
            <span title={getProfileName()}>
              {truncate(getProfileName(), 17)}
            </span>
          </div>
          <div className='sidebarTara__profile--content-right'>
            <CustomSVGs svg={arrowHeadLeftSVG} />
          </div>
          <AnimatePresence>
            {isDropdownOpen && <SidebarProfileTheme2 />}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default SidebarTara;
